import React, { useCallback, useMemo, useRef, useState } from "react";
import { useFocusEffect } from '@react-navigation/native';
import { StyleSheet } from 'react-native'
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import i18n from "utils/i18n";
import ThemedBlock from 'components/atoms/ThemedBlock'
import Text from "theme/Text";
import { colorList } from 'utils/colors'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import _ from "lodash";
import AccountStorage from 'utils/accounts/AccountStorage'
import useAccountSwitch from 'utils/accounts/use-account-switch'
import Button from 'theme/Button'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import TextDivider from "components/atoms/TextDivider";
import LoadingComponent from 'components/atoms/Loading'
import UserService from 'services/user'
import store from 'services/redux/store'
import { resetData } from 'services/redux/data/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import { resetManagement } from 'services/redux/management/actions'
import ListItemAccount from 'components/atoms/ListItemAccount'
import AccountList from 'components/molecules/AccountList'
import VStack from 'components/atoms/stack/VStack'
import { ThemedValues } from 'styles/themed'
import HelperAlerts from 'utils/HelperAlerts'

const HomeMoreAccounts = () => {
	
	const employeeName = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE]?.name;
	} )
	
	const [ currentAccountIdentifier, setCurrentAccountIdentifier ] = useState( null );
	const [ accounts, setAccounts ] = useState( {} );
	const [ isAdding, setIsAdding ] = useState( false );
	const { isSwitching, switchAccount } = useAccountSwitch( currentAccountIdentifier );
	const [ isLoading, setIsLoading ] = useState( true );
	const saving = useRef( false );

	const showSaveError = () => {
		return (
			HelperAlerts.alert(
				i18n.t( 'pages.more.accounts.save_error_title' ),
				i18n.t( 'pages.more.accounts.save_error' ),
			)
		);
	};
	

	useFocusEffect( useCallback( () => {
		let active = true;
		Promise.all([
			AccountStorage.getAccounts(),
			SecureStoreService.get( storeKeys.ACCOUNT_IDENTIFIER ),
		]).then( async ([ savedAccounts, accountIdentifier ]) => {
			// Repair the current account when an earlier encrypted save failed.
			if( active && accountIdentifier && !savedAccounts[ accountIdentifier ] ) {
				const account = await AccountStorage.saveCurrentAccount({
					employeeName,
					companyName: HelperSetting.get( settingsConstants.COMPANY_NAME ),
				});
				savedAccounts = { ...savedAccounts, [ accountIdentifier ]: account };
			}
			if( active ) {
				setAccounts( savedAccounts );
				setCurrentAccountIdentifier( accountIdentifier );
			}
		}).catch( () => {
			if( active ) showSaveError();
		}).finally( () => {
			if( active ) setIsLoading( false );
		});
		return () => {
			active = false;
		};
	}, [ employeeName ] ));
	
	const disableMultiAccount = () => {
		AccountStorage.clearAccounts().then( () => {
			setAccounts( {} );
			SecureStoreService.delete( storeKeys.ACCOUNT_IDENTIFIER ).then( () => {
				setCurrentAccountIdentifier( null );
			});
		});
	}
	
	const enableMultiAccount = async () => {
		if( saving.current ) return false;
		saving.current = true;
		try {
			const account = await AccountStorage.saveCurrentAccount({
				employeeName,
				companyName: HelperSetting.get( settingsConstants.COMPANY_NAME ),
			});
			const identifier = account[ storeKeys.ACCOUNT_IDENTIFIER ];
			setAccounts( previous => {
				return {
					...previous,
					[identifier]: account
				};
			} );
			setCurrentAccountIdentifier( identifier );
			return true;
		} catch {
			showSaveError();
			return false;
		} finally {
			saving.current = false;
		}
	}


	const addAccount = async () => {
		if( !await enableMultiAccount() ) return;
		setIsAdding( true );
		logout();
	}
	
	const logout = () => {
		UserService.logout({
			restart: true,
			resetData: () => {
				return store.dispatch(resetData());
			},
			resetManagement: () => {
				return store.dispatch(resetManagement());
			},
			resetAuthentication: () => {
				return store.dispatch(resetAuthentication());
			},
			resetCart: () => {
				return store.dispatch(resetCart());
			},
		});
	}
	
	const removeAccount = useCallback( async ( account ) => {
		const identifier = account[ storeKeys.ACCOUNT_IDENTIFIER ];
		await AccountStorage.removeAccount( identifier );
		setAccounts( previous => {
			return _.omit(previous, identifier);
		} );
	}, [] );

	const { activeAccount, otherAccounts, accountDisplay } = useMemo( () => {
		const list = Object.values( accounts );
		const differs = key => {
			return new Set(list.map(account => {
				return account[key] ?? '';
			})).size > 1;
		};
		return {
			activeAccount: list.find( account => {
				return account[storeKeys.ACCOUNT_IDENTIFIER] === currentAccountIdentifier;
			} ),
			otherAccounts: list.filter( account => {
				return account[storeKeys.ACCOUNT_IDENTIFIER] !== currentAccountIdentifier;
			} ),
			accountDisplay: {
				isMultiCompanyName: differs( storeKeys.COMPANY_NAME ),
				isMultiDomain: differs( storeKeys.DOMAIN ),
				isMultiDomainPrefix: differs( storeKeys.DOMAIN_PREFIX ),
			},
		};
	}, [ accounts, currentAccountIdentifier ] );

	return (
		<ThemedSafeAreaComponentTop
			top={false}>
			
			<ThemedLayout
				topNavigation={ true }
				showBackButton={ true }
				title={ i18n.t( 'titles.more_accounts' ) }>
				
				{ currentAccountIdentifier === null && (
					<Button
						onPress={ enableMultiAccount }
						disabled={ isLoading }>
						
						{ i18n.t( 'pages.more.accounts.enable_multi_account') }
				   </Button>
				)}
				
				<VStack
					space={ ThemedValues.stackSpace }>
					
					<VStack>
						<TextDivider text={ i18n.t( 'pages.more.accounts.active_account') } />
						
						{ activeAccount && (
							<ListItemAccount account={ activeAccount } />
						) }
					</VStack>
					
					{ otherAccounts.length > 0 && (
						<VStack>
							<TextDivider text={ i18n.t( 'pages.more.accounts.other_accounts') } />
							
							<AccountList
								accounts={ otherAccounts }
								{ ...accountDisplay }
								currentAccountIdentifier={ currentAccountIdentifier }
								removeAccount={ removeAccount }
								switchAccount={ switchAccount }
							/>
						</VStack>
					)}
					
					<VStack>
						<Button onPress={ addAccount } disabled={ isLoading }>{ i18n.t( 'pages.more.accounts.add_account') }</Button>
						<ThemedBlock shadow smallPadding>
							<Text style={ styles.note }>{ i18n.t( 'pages.more.accounts.will_logout') }</Text>
						</ThemedBlock>
					</VStack>
					
					<Button onPress={ disableMultiAccount } status={"danger"} appearance={ "ghost"}>{ i18n.t( 'pages.more.accounts.remove_all_accounts') }</Button>
				</VStack>
			</ThemedLayout>
			
			{ ( isSwitching || isAdding || isLoading ) && (
				<LoadingComponent fullscreen={ true } size={ 'l' } />
			)}
		</ThemedSafeAreaComponentTop>
	);
}

const styles = StyleSheet.create({
	note: {
		fontSize: 12,
		color: colorList.BLACK.light,
		fontStyle: 'italic'
	},
});

export default HomeMoreAccounts;
